import Ionicons from "@expo/vector-icons/Ionicons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, StyleSheet, TextInput, View } from "react-native";

import {
  HelpButton,
  HelpScreen,
  HelpSection,
  InfoNote,
} from "@/components/help/help-ui";
import { ThemedText } from "@/components/themed-text";
import { Radius, Spacing } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { supportApi } from "@/api";
import { notify } from "@/lib/dialog";
import { useAuthStore } from "@/state/auth-store";
import i18n, { type TranslationKey } from "@/i18n";

const MESSAGE_MAX = 500;

type Category = { value: string; labelKey: TranslationKey };

const REPORT_CATEGORIES: Category[] = [
  { value: "Unsafe behavior", labelKey: "help.contact.categories.unsafe" },
  { value: "Harassment", labelKey: "help.contact.categories.harassment" },
  { value: "Fake profile", labelKey: "help.contact.categories.fakeProfile" },
  { value: "Spam", labelKey: "help.contact.categories.spam" },
  { value: "Bug", labelKey: "help.contact.categories.bug" },
  { value: "Billing issue", labelKey: "help.contact.categories.billingIssue" },
  { value: "Other", labelKey: "help.contact.categories.other" },
];

const CONTACT_CATEGORIES: Category[] = [
  { value: "Bug", labelKey: "help.contact.categories.bug" },
  {
    value: "Safety concern",
    labelKey: "help.contact.categories.safetyConcern",
  },
  { value: "Billing/Plus", labelKey: "help.contact.categories.billingPlus" },
  {
    value: "Account/privacy",
    labelKey: "help.contact.categories.accountPrivacy",
  },
  { value: "Feedback", labelKey: "help.contact.categories.feedback" },
  { value: "Other", labelKey: "help.contact.categories.other" },
];

type Source = "report" | "safety" | "feedback" | "contact";

const SOURCES: Record<
  Source,
  {
    titleKey: TranslationKey;
    promptKey?: TranslationKey;
    categories: Category[];
    urgentNote: boolean;
  }
> = {
  report: {
    titleKey: "help.contact.reportTitle",
    promptKey: "help.contact.reportPrompt",
    categories: REPORT_CATEGORIES,
    urgentNote: true,
  },
  safety: {
    titleKey: "help.contact.safetyTitle",
    promptKey: "help.contact.reportPrompt",
    categories: REPORT_CATEGORIES,
    urgentNote: true,
  },
  contact: {
    titleKey: "help.contact.contactTitle",
    promptKey: "help.contact.helpPrompt",
    categories: CONTACT_CATEGORIES,
    urgentNote: true,
  },
  feedback: {
    titleKey: "help.contact.feedbackTitle",
    categories: [
      { value: "Feedback", labelKey: "help.contact.categories.feedback" },
    ],
    urgentNote: false,
  },
};

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export default function ContactForm() {
  useTranslation();
  const c = useTheme();
  const router = useRouter();
  const params = useLocalSearchParams<{ source?: string; category?: string }>();
  const userEmail = useAuthStore((s) => s.user?.email);

  const source: Source =
    params.source === "report" ||
    params.source === "safety" ||
    params.source === "feedback"
      ? params.source
      : "contact";
  const config = SOURCES[source];

  const initialCategory =
    params.category &&
    config.categories.some((option) => option.value === params.category)
      ? params.category
      : config.categories[0].value;

  const [category, setCategory] = useState(initialCategory);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState(userEmail ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const canSend =
    !submitting && message.trim().length > 0 && isValidEmail(email);

  async function send() {
    if (!canSend) return;
    setSubmitting(true);
    try {
      await supportApi.submit({
        category,
        message: message.trim(),
        responseEmail: email.trim(),
      });
      setSent(true);
    } catch {
      notify(
        i18n.t("help.contact.sendError"),
        i18n.t("help.contact.sendErrorBody"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <HelpScreen title={i18n.t(config.titleKey)}>
        <View style={styles.successWrap}>
          <View
            style={[styles.successIcon, { backgroundColor: c.successTint }]}
          >
            <Ionicons name="checkmark" size={34} color={c.success} />
          </View>
          <ThemedText variant="title" center>
            {i18n.t("help.contact.received")}
          </ThemedText>
          <ThemedText variant="body" tone="secondary" center>
            {i18n.t("help.contact.reviewSoon")}
          </ThemedText>
          <View style={styles.successButton}>
            <HelpButton
              label={i18n.t("help.contact.done")}
              onPress={() => router.back()}
            />
          </View>
        </View>
      </HelpScreen>
    );
  }

  const showCategoryPicker = config.categories.length > 1;

  return (
    <HelpScreen title={i18n.t(config.titleKey)}>
      {showCategoryPicker ? (
        <HelpSection
          heading={config.promptKey ? i18n.t(config.promptKey) : undefined}
        >
          <View style={styles.chips}>
            {config.categories.map((option) => {
              const active = option.value === category;
              return (
                <Pressable
                  key={option.value}
                  accessibilityRole="button"
                  accessibilityState={{ selected: active }}
                  onPress={() => setCategory(option.value)}
                  style={[
                    styles.chip,
                    {
                      backgroundColor: active ? c.tint : c.surface,
                      borderColor: active ? c.tint : c.border,
                    },
                  ]}
                >
                  <ThemedText
                    variant="caption"
                    style={{ color: active ? c.onTint : c.text }}
                  >
                    {i18n.t(option.labelKey)}
                  </ThemedText>
                </Pressable>
              );
            })}
          </View>
        </HelpSection>
      ) : null}

      <HelpSection heading={i18n.t("help.contact.message")}>
        <TextInput
          value={message}
          onChangeText={(text) => setMessage(text.slice(0, MESSAGE_MAX))}
          placeholder={i18n.t("help.contact.messagePlaceholder")}
          placeholderTextColor={c.textMuted}
          multiline
          maxLength={MESSAGE_MAX}
          style={[
            styles.messageInput,
            {
              color: c.text,
              borderColor: c.border,
              backgroundColor: c.surface,
            },
          ]}
        />
        <ThemedText variant="caption" tone="muted" style={styles.counter}>
          {message.length}/{MESSAGE_MAX}
        </ThemedText>
      </HelpSection>

      <HelpSection heading={i18n.t("help.contact.replyHeading")}>
        <TextInput
          value={email}
          onChangeText={setEmail}
          placeholder={i18n.t("help.contact.emailPlaceholder")}
          placeholderTextColor={c.textMuted}
          autoCapitalize="none"
          keyboardType="email-address"
          inputMode="email"
          style={[
            styles.emailInput,
            {
              color: c.text,
              borderColor: c.border,
              backgroundColor: c.surface,
            },
          ]}
        />
      </HelpSection>

      {config.urgentNote ? (
        <InfoNote icon="alert-circle-outline">
          {i18n.t("help.contact.urgent")}
        </InfoNote>
      ) : null}

      <HelpButton
        label={i18n.t(
          submitting ? "help.contact.sending" : "help.contact.send",
        )}
        icon="send-outline"
        onPress={send}
        disabled={!canSend}
      />
    </HelpScreen>
  );
}

const styles = StyleSheet.create({
  chips: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: Spacing.sm,
    marginTop: Spacing.xs,
  },
  chip: {
    minHeight: 38,
    paddingHorizontal: Spacing.md,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: Radius.pill,
    borderWidth: 1,
  },
  messageInput: {
    minHeight: 132,
    borderWidth: 1,
    borderRadius: Radius.lg,
    padding: Spacing.md,
    fontSize: 15,
    textAlignVertical: "top",
  },
  counter: { alignSelf: "flex-end" },
  emailInput: {
    minHeight: 48,
    borderWidth: 1,
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.md,
    fontSize: 15,
  },
  successWrap: {
    alignItems: "center",
    gap: Spacing.md,
    paddingVertical: Spacing.xxl,
  },
  successIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: Spacing.sm,
  },
  successButton: { width: "100%", marginTop: Spacing.lg },
});
